import type { BubbleBox, PanelView } from './manga';

/**
 * Turning a chapter into pages for a reader that shows one at a time: the app's paged reader and the
 * web reader for shared chapters cut the same render the same way.
 */

/** What page-cutting needs to know about a panel. */
export type PagePanel = Pick<PanelView, 'panelId' | 'width' | 'height' | 'lettering' | 'page'>;

/** One spread: a drawn page, or panels stacked to fill the screen. `first` is its first panel's place in the render. */
export interface ArtPage<P extends PagePanel = PanelView> {
  key: string;
  panels: P[];
  first: number;
}

/** Gap between panels stacked on one page. */
export const STACK_GAP = 10;

const aspectOf = (p: PagePanel) => (p.width > 0 && p.height > 0 ? p.width / p.height : 1);

/** How tall these panels stand stacked at this width. */
export const stackHeight = (panels: PagePanel[], width: number) => panels.reduce((h, p) => h + width / aspectOf(p), 0) + STACK_GAP * (panels.length - 1);

/**
 * The render cut into pages. A page the image model drew is a page; panels lettered by the reader
 * keep the page the server gave them (an illustrated chapter's composed page), else consecutive
 * panels share a page while they fit the screen at full width, or at `minScale` of it (a reader
 * with less height to spare can narrow panels a little rather than show one on a mostly empty page).
 */
export function artPages<P extends PagePanel>(panels: P[], width: number, room: number, minScale = 1): ArtPage<P>[] {
  const pages: ArtPage<P>[] = [];
  panels.forEach((p, i) => {
    const last = pages.at(-1);
    const prev = last?.panels.at(-1);
    const joins =
      !!last &&
      !!prev &&
      p.lettering === 'overlay' &&
      prev.lettering === 'overlay' &&
      (p.page !== null ? p.page === prev.page : prev.page === null && stackHeight([...last.panels, p], width * minScale) <= room);
    if (joins) last.panels.push(p);
    else pages.push({ key: p.panelId, panels: [p], first: i });
  });
  return pages;
}

/** The widest a page's stacked panels can be and still fit the screen's height. */
export function fitPageWidth(panels: PagePanel[], width: number, height: number) {
  const perWidth = panels.reduce((s, p) => s + (p.width > 0 && p.height > 0 ? p.height / p.width : 1), 0);
  return Math.min(width, (height - STACK_GAP * (panels.length - 1)) / perWidth);
}

/** The page holding this panel. */
export const pageOfPanel = (pages: ArtPage<PagePanel>[], panel: number) => Math.max(0, pages.filter((pg) => pg.first <= panel).length - 1);

/** Room left under a balloon for its tail before the next one starts. */
export const TAIL_ROOM = 14;

/**
 * Where each balloon of a panel starts once its real height is known: at the server's position, or
 * just below any earlier balloon it shares columns with, whichever is lower (a bigger reading size or
 * another typeface can make one grow into the next). `heights` holds what has been measured so far.
 */
export function balloonTops(boxes: BubbleBox[], panelWidth: number, panelHeight: number, heights: ReadonlyArray<number | undefined>): number[] {
  const out: number[] = [];
  boxes.forEach((a, i) => {
    let y = a.y * panelHeight;
    for (let j = 0; j < i; j++) {
      const b = boxes[j]!;
      const sharesColumns = a.x * panelWidth < (b.x + b.w) * panelWidth && b.x * panelWidth < (a.x + a.w) * panelWidth;
      const h = heights[j];
      if (sharesColumns && h) y = Math.max(y, out[j]! + h + (b.tail === 'none' ? 6 : TAIL_ROOM));
    }
    out.push(y);
  });
  return out;
}
